import { usableTokens } from "./utils";

type Asset = { id: number; symbol: string; contract_address?: string; balance: string | number; spendable?: string | null };
type WalletLike = { id: number; network?: { id?: number | null } | null; assets: Asset[]; isLoading?: boolean; isError?: boolean };
type CurrencyLike = { id?: number | string | null; symbol?: string | null; contract_address?: string | null };

const NATIVE = /^0x0{40}$/i;
const lower = (v: unknown) => String(v ?? "").trim().toLowerCase();

/**
 * WHAT THE USER CAN SPEND OF ONE CURRENCY ON ONE NETWORK, from their own wallet balance — the `spendable` part (an open
 * offer, a bid or a swap already holds the rest; see `usableTokens`). A wrapped coin (WPOL, WETH) also counts the coin
 * it wraps on that network, because paying in it wraps the coin first. Null when it cannot be known yet — no wallet
 * on that network, or its balance still loading or failed — so a check never blocks on a guess.
 */
export function spendableOn(wallets: WalletLike[], networkId: number | null | undefined, currency: CurrencyLike | null | undefined): { amount: number; walletId: number } | null {
  if (networkId == null || !currency) return null;
  const wallet = wallets.find((w) => Number(w.network?.id) === Number(networkId));
  if (!wallet || wallet.isLoading || wallet.isError) return null;
  const sym = lower(currency.symbol);
  const contract = lower(currency.contract_address);
  const asset = wallet.assets.find((a) => (currency.id != null && Number(a.id) === Number(currency.id)) || (contract && lower(a.contract_address) === contract));
  let amount = asset ? usableTokens(asset) : 0;
  if (sym.length > 1 && sym.startsWith("w")) {
    const coin = wallet.assets.find((a) => lower(a.symbol) === sym.slice(1) && (!a.contract_address || NATIVE.test(a.contract_address)));
    if (coin) amount += usableTokens(coin);
  }
  return { amount, walletId: wallet.id };
}

/** Short when an amount is known and is more than what can be spent; never when either is unknown. */
export function isShort(need: number | string | null | undefined, have: { amount: number } | null): boolean {
  const n = Number(need);
  return have !== null && Number.isFinite(n) && n > 0 && n > have.amount + 1e-12;
}
